import { useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Controller, useForm } from 'react-hook-form'
import { StepContent } from '../stepper/StepContent'
import { useDispatch } from 'react-redux'
import { Alert, Button, Slider, Typography } from '@mui/material'
import { RatedForm } from '@/types/Questions'
import { CreatorButtonsGo } from './CreatorButtonsGo'
import clsx from 'clsx'
import { CreatorGoSteps, markStepCompleted, nextStep } from '@/slices/creatorGo'
import { CreatorGoProps } from './CreatorGo'
import { IQuestion, IWeightedQuestionAnswer, MeetingType, WeightedAnswer } from '@namuho/types'
import { useQuestions } from '@/hooks/questions/useQuestions'
import { usePreferences } from '@/hooks/preferences/usePreferences'
import { ratedMarks } from '@/helpers/ratedMarks'
import { StepTitle } from '../stepper/StepTitle'
import { Check, QuestionMark } from '@mui/icons-material'

const useStyles = makeStyles()((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        fontSize: '1rem',
        [theme.breakpoints.down('md')]: {
            justifyContent: 'flex-start',
        },
    },
    legend: {
        fontSize: '1rem',
        padding: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
            textAlign: 'left',
        },
    },
    answer: {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '2fr 3fr min-content',
        alignItems: 'center',
        padding: theme.spacing(1),
        gap: theme.spacing(2),
        transition: 'max-height 0.3s ease-in-out',
        overflow: 'hidden',
        [theme.breakpoints.down('md')]: {
            display: 'flex',
            flexDirection: 'column',
        },
    },
    checkIcon: {
        height: '1.5rem',
        marginBottom: theme.spacing(2),
    },
    label: {
        display: 'block',
        width: '100%',
        textAlign: 'start',
    },
    slider: {
        [theme.breakpoints.down('md')]: {
            maxWidth: '90%',
        },
    },
    markLabel: {
        fontSize: '0.9rem',
        color: 'silver',
    },
    markLabelActive: {
        color: theme.palette.primary.main,
    },
    alert: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
    },
}))

export const RatedQuestionsGo = (props: CreatorGoProps) => {
    const { dateType } = props
    const { control, setValue, getValues } = useForm<RatedForm>({})
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const { weighted, isLoading: isLoadingQuestions } = useQuestions()
    const [answeredQuestions, setAnsweredQuestions] = useState<string[]>([])
    const [numbersOfUnasnweredQuestions, setNumbersOfAnansweredQuestions] = useState<number[]>([])
    const [warningDissmissed, setWarningDissmissed] = useState(false)
    const [showWarning, setShowWarning] = useState(false)
    const {
        isLoading: isLoadingPreferences,
        friendlyPreferences,
        romanticPreferences,
        groupPreferences,
        updateFriendlyPreferences,
        updateRomanticPreferences,
        updateGroupPreferences,
    } = usePreferences()

    const currentSetOfWeighted: IQuestion[] = weighted.filter((question: IQuestion) => question.meetingType === dateType)

    const handleAnsweredQuestion = (questionId: string) => {
        setShowWarning(false)
        setNumbersOfAnansweredQuestions([])
        setAnsweredQuestions((prev) => [...new Set([...prev, questionId])])
    }

    const handleWarningDismiss = () => {
        setWarningDissmissed(true)
    }

    const handleNext = async () => {
        const values = getValues()
        if (answeredQuestions.length < currentSetOfWeighted.length && !warningDissmissed) {
            setShowWarning(true)
            // set numbersOfUnasnweredQuestions
            currentSetOfWeighted.forEach((question, index) => {
                if (!answeredQuestions.includes(question.id)) {
                    setNumbersOfAnansweredQuestions((prev) => [...prev, index + 1])
                }
            })

            return null
        }
        const answers: IWeightedQuestionAnswer[] = Object.keys(values).map((key) => ({
            questionId: key,
            answer: (values[key] as WeightedAnswer) || 3,
        }))
        if (dateType === MeetingType.Friendly) {
            await updateFriendlyPreferences({
                ...friendlyPreferences,
                weightedQuestions: answers,
            })
        }
        if (dateType === MeetingType.Romantic) {
            await updateRomanticPreferences({
                ...romanticPreferences,
                weightedQuestions: answers,
            })
        }
        if (dateType === MeetingType.Group) {
            await updateGroupPreferences({
                ...groupPreferences,
                weightedQuestions: answers,
            })
        }
        dispatch(markStepCompleted({ dateType: dateType, step: CreatorGoSteps.RATED_QUESTIONS }))
        dispatch(nextStep(dateType))
        return null
    }

    useEffect(() => {
        try {
            if (dateType === MeetingType.Friendly && friendlyPreferences) {
                const answers = friendlyPreferences.weightedQuestions
                answers?.forEach((answer: IWeightedQuestionAnswer) => {
                    setAnsweredQuestions((prev) => [...new Set([...prev, answer.questionId])])
                    setValue(answer.questionId, answer.answer)
                })
            }
            if (dateType === MeetingType.Romantic && romanticPreferences) {
                const answers = romanticPreferences.weightedQuestions
                answers?.forEach((answer: IWeightedQuestionAnswer) => {
                    setAnsweredQuestions((prev) => [...new Set([...prev, answer.questionId])])
                    setValue(answer.questionId, answer.answer)
                })
            }
            if (dateType === MeetingType.Group && groupPreferences) {
                const answers = groupPreferences.weightedQuestions
                answers?.forEach((answer: IWeightedQuestionAnswer) => {
                    setAnsweredQuestions((prev) => [...new Set([...prev, answer.questionId])])
                    setValue(answer.questionId, answer.answer)
                })
            }
        } catch (e) {
            console.log(e)
        }
    }, [dateType, friendlyPreferences, groupPreferences, romanticPreferences, setValue])

    return (
        <StepContent
            title={<StepTitle title="Wie sehr treffen folgende Sätze auf dich zu?" />}
            content={
                <>
                    <Typography className={classes.legend} variant="body1">
                        1 = Stimmt überhaupt nicht, 5 = Stimmt vollkommen
                    </Typography>

                    <form className={classes.root}>
                        {currentSetOfWeighted.map((question, index) => (
                            <label
                                onClick={() => handleAnsweredQuestion(question.id)}
                                key={question.id}
                                htmlFor={question.id}
                                className={clsx(classes.answer)}
                            >
                                <span className={classes.label}>{`${index + 1}/${currentSetOfWeighted.length} ${question.label}`}</span>
                                <Controller
                                    name={question.id}
                                    control={control}
                                    render={(renderProps) => (
                                        <Slider
                                            classes={{ root: classes.slider, markLabel: classes.markLabel, markLabelActive: classes.markLabelActive }}
                                            {...renderProps}
                                            onChange={(_, value) => {
                                                handleAnsweredQuestion(question.id)
                                                renderProps.field.onChange(value)
                                            }}
                                            value={renderProps.field.value || 3}
                                            marks={ratedMarks}
                                            max={5}
                                            min={1}
                                            step={1}
                                        />
                                    )}
                                />
                                {answeredQuestions.includes(question.id) ? (
                                    <Check color="success" className={classes.checkIcon} />
                                ) : (
                                    <QuestionMark color="primary" className={classes.checkIcon} />
                                )}
                            </label>
                        ))}
                    </form>
                    {showWarning && !warningDissmissed && (
                        <Alert
                            className={classes.alert}
                            severity="warning"
                            action={
                                <Button color="inherit" size="small" onClick={handleWarningDismiss}>
                                    Ja, ich bin mir sicher
                                </Button>
                            }
                        >
                            Du hast einige der Antworten nicht berührt. Bist du sicher, dass du sie mit der Standardantwort belassen willst? Nicht
                            berührte Frage(n): {numbersOfUnasnweredQuestions.join(', ')}
                        </Alert>
                    )}
                </>
            }
            buttons={
                <CreatorButtonsGo
                    dateType={dateType}
                    nextStepActive={true}
                    previousStepActive={true}
                    isFirst={false}
                    isLast={false}
                    handleNext={handleNext}
                />
            }
        />
    )
}
